import md5 from 'md5'
import { all, call, put, select, takeLeading } from 'redux-saga/effects'
import * as endpointConstants from '../constants/endpoints'
import * as folderConstants from '../constants/folder'
import SessionTimeoutError from '../modules/SessionTimeoutError'
import { generateDefaultContentRoles } from '../modules/utils'
import * as appActions from './appActions'
import * as endpointActions from './endpoint'
import * as folderActions from './folder'
import * as userActions from './user'

/*
 * Shared endpoints (SHARED-ENDPOINTS-PLAN.md Phase 2, provider side).
 *
 * A shared endpoint is an ordinary content endpoint (SE-<md5> id,
 * shared: true) that the backend exports to S3 on every snapshot rebuild so
 * other BAM! instances can consume it (grants are provisioned by Pop Art —
 * Phase 3). Publishing into it uses the regular Library publish workflow;
 * this saga only handles creation. Deletion reuses the generic
 * deleteEndpoint flow (unpublish every asset + recycle bin).
 */

export const SAVE_SHARED_ENDPOINT = 'SAVE_SHARED_ENDPOINT'

export const saveSharedEndpoint = (values) => ({
  type: SAVE_SHARED_ENDPOINT,
  values, // { name, description }
})

export function* watchSaveSharedEndpoint() {
  yield takeLeading(SAVE_SHARED_ENDPOINT, saveSharedEndpointSaga)
}

export function* saveSharedEndpointSaga(action) {
  try {
    yield put({ type: appActions.INITIATE_ASYNC, showSpinner: true })

    const { values } = action
    const endpointId = `SE-${md5(`${values.name}${Date.now()}`)}`

    const { client, id: userId } = yield select((state) => state.user)
    const { byId: foldersById } = yield select((state) => state.folders)
    const timestamp = yield call(Date.now)
    const rootFolderId = yield call(md5, `${userId}${timestamp}`)

    // Sequential: the endpoint references the root folder it gets
    yield call(endpointActions.addEndpointSaga, {
      type: endpointActions.ADD_ENDPOINT,
      client,
      name: values.name,
      options: {
        actionIcon: 'folder-add',
        allowPublishing: true,
        badges: [
          endpointActions.PENDING_APPROVAL,
          endpointActions.PENDING_REMOVAL,
        ],
        description: values.description || '',
        icon: 'fa-share-alt',
        id: endpointId,
        privilege: 'addfolder',
        rootFolders: [rootFolderId],
        type: endpointConstants.SHARED_ENDPOINT_TYPE,
        shared: true,
        sharedMeta: {},
      },
    })

    const groups = yield select((state) => state.permissions.groups)
    const contentRoles = yield call(
      generateDefaultContentRoles,
      groups,
      endpointConstants.SHARED_ENDPOINT_TYPE,
    )

    // The consumer instance shows the endpoint name as the root-level
    // Library folder, so the provider-side root folder carries it too
    yield call(folderActions.addFolderSaga, {
      type: folderActions.ADD_FOLDER,
      name: values.name,
      parent: {},
      owner: userId,
      client,
      permissions: contentRoles,
      path: [],
      allFolders: foldersById,
      folderType: folderConstants.SHARED_FOLDER_TYPE,
      id: rootFolderId,
    })
  } catch (err) {
    console.error(err)
    if (err instanceof SessionTimeoutError) {
      yield put({ type: userActions.LOGOUT })
    }
  } finally {
    yield put({ type: appActions.COMPLETE_ASYNC })
  }
}

export default all([watchSaveSharedEndpoint()])
